/**
 * Local updates to the manager's group tree (HierarchicalGroupList), so an
 * edit or delete shows without a reload (namat-mobile#73). Pure.
 */

export interface GroupTreeNode<T> {
  id: string;
  name: string;
  level?: number;
  children: T[];
}

/** Merges `patch` into the node with `id`, wherever it sits in the tree. */
export function patchGroupNode<T extends GroupTreeNode<T>>(nodes: T[], id: string, patch: Partial<T>): T[] {
  return nodes.map((node) =>
    node.id === id
      ? { ...node, ...patch }
      : { ...node, children: patchGroupNode(node.children || [], id, patch) }
  );
}

function withLevel<T extends GroupTreeNode<T>>(node: T, level: number): T {
  return { ...node, level, children: (node.children || []).map((c) => withLevel(c, level + 1)) };
}

/**
 * Removes the node with `id`. Its children are NOT removed with it: deleting
 * a section leaves its subgroups in Firestore, and getGroupHierarchy lists a
 * group whose parent no longer exists at the top level. So they move to the
 * top level here too, sorted by name the way getGroupHierarchy sorts.
 */
export function removeGroupKeepingChildren<T extends GroupTreeNode<T>>(nodes: T[], id: string): T[] {
  const orphans: T[] = [];
  const strip = (list: T[]): T[] =>
    list
      .filter((node) => {
        if (node.id !== id) return true;
        orphans.push(...(node.children || []));
        return false;
      })
      .map((node) => ({ ...node, children: strip(node.children || []) }));

  const kept = strip(nodes);
  if (orphans.length === 0) return kept;
  return [...kept, ...orphans.map((o) => withLevel(o, 0))].sort((a, b) => a.name.localeCompare(b.name));
}
